import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import { useQueryClient } from '@tanstack/react-query';
import ReactGA from 'react-ga4';
import Moment from 'moment';
import isEqual from 'lodash.isequal';
import _ from 'lodash';
import { bindActionCreators } from 'redux';

import {
    DEFAULT_PAGE_SIZE,
    MOBILE_WINDOW_BREAKPOINT,
    TASK_LIST_VIEWS as VIEWS,
    TASK_LIST_URL_PARAMS as URLParams,
    GANTT_ZOOM_LEVELS,
    DATE_FORMAT,
    TASK_LIST_MENU_ITEMS,
    CORE_STATUSES,
    EVENT_TYPE,
} from '../../../constants/constants';
import { actions as locationTaskActions } from '../../../actions/locationTaskActions';
import { actions as subscriptionActions } from '../../../actions/subscriptionActions';
import { actions as locationDashboardActions } from '../../../actions/locationDashboardActions';
import { actions as filesActions } from '../../../actions/filesActions';

import Notification from '../../Common/Notification';
import ErrorFallback from '../../Common/ErrorFallback';
import LoadingIndicator from '../../Common/LoadingIndicator/LoadingIndicator';
import URLSearchParams from '@ungap/url-search-params';

import TaskListHeader from './TaskListHeader/TaskListHeader';
import ListView from './ListView/ListView';
import ListViewMobile from './ListViewMobile/ListViewMobile';
import KanbanView from '../KanbanView';
import CalendarView from '../Calendar/CalendarView';
import GanttChartView2 from '../GanttChart/GanttChartView2';
import ResourceAllocationView from '../ResourceAllocation/ResourceAllocationView';
import TaskDetailManager from '../TaskDetail/TaskDetailManager';
import LocationTaskSidebar from '../TaskDetail/LocationTaskSidebar/LocationTaskSidebar';
import QuickCreateTaskSidebar from '../TaskDetail/QuickCreateTaskSidebar/QuickCreateTaskSidebar';
import ColumnFiltersDrawer from '../ColumnFiltersDrawer';
import BulkUpdateResponsibleUserDrawer from '../BulkUpdateResponsibleUserDrawer';
import BulkUpdateStatusDrawer from '../BulkUpdateStatusDrawer';
import AssignTasksDrawer from '../../LocationDashboard/Widgets/AssignTasksDrawer';
import ShowFiltersDrawer from '../ShowFIltersDrawer';
import ReportFooter from '../../Common/ReportFooter/ReportFooter';
import Modal61 from '../../Common/Modal/Modal61';
import InputText from '../../Common/InputText';

import { ConvertDayOfWeekToCalendarStartDay } from '../../../helpers/calendar';
import { buildTaskListQueryString, determineLocationKeysForFilters, isAllLocationsMode } from '../../../helpers/taskListHelpers';

import {
    useLoadListFilters,
    useLoadColumnFilters,
    useLoadInitialLocationSelectorFilters,
    useSearchLocationTasks,
    useSearchResponsibleUsers,
    useUpdateDates,
    useUpdateContingent,
    useUpdateSavedColumns,
    useSaveCustomFilters,
    useBulkShiftDates,
    useUpdateIsActive,
    useUpdateFollowingTasks,
    useUpdateStatus,
    useUpdateResponsibleUser,
    getCustomFilter,
    invalidateSearches,
    invalidateSearch,
    invalidateColumnFilters,
} from '../../../apis/locationTasks';
import { useSaveUserColumns } from '../../../apis/users';
import { type SaveUserColumnsRequest } from '../../../apis/users/user.types';
import { useDownloadFile } from '../../../apis/files';
import {
    type LocationTask,
    type TaskSearchRequest,
    type ColumnFiltersResponse,
} from '../../../apis/locationTasks/locationTask.types';

import './TaskListPage.scss';

// Cast JS components that have stricter TypeScript inferred props than what we pass
const AnyTaskDetailManager = TaskDetailManager as React.ComponentType<any>;

// ---- Types ----

type LocationTaskWithCheck = LocationTask & { isChecked: boolean };

interface ShiftDateValidationResult {
    isValid: boolean;
    message?: string;
}

// Column filters shape expected by child components (API returns targetDates, children expect targetDate)
type ChildColumnFilters = Omit<ColumnFiltersResponse, 'targetDates'> & { targetDate: ColumnFiltersResponse['targetDates'] };

// ---- Default search params factory ----

const makeDefaultSearchParams = (locationKeys: string[]): TaskSearchRequest => ({
    isMobile: false,
    pageSize: DEFAULT_PAGE_SIZE,
    pageNumber: 1,
    sortBy: 'targetDate',
    sortDir: 'asc',
    name: '',
    locationKeys,
    taskId: '',
    projectIds: [],
    targetDateStart: null,
    targetDateEnd: null,
    startDateStart: null,
    startDateEnd: null,
    responsibleUserName: '',
    statusIds: [],
    departmentIds: [],
    disciplineIds: [],
    communityIds: [],
    phaseIds: [],
    tagIds: [],
    additionalInfo: '',
    baselineName: '',
    recentlyUpdated: null,
    isCalendarView: false,
    isFollowedByCurrentUser: '',
});

/**
 * Read a repeated id query param as positive integers.
 *
 * Numbers, not strings: columnFiltersRaw ids are numbers and the "prune stale ids" effect compares
 * them with ===, the request schema declares z.number().int(), and a string id hashes to a
 * different query key than its numeric twin. The filter drops junk that Number() would otherwise
 * turn into a plausible-looking id — '?s=' becomes 0, which filters out every task, and a
 * non-numeric value becomes NaN, which JSON.stringify writes as null in both the key and the body.
 */
const parseIdListParam = (sp: URLSearchParams, key: string): number[] =>
    sp.getAll(key).map(Number).filter((id) => Number.isInteger(id) && id > 0);

const ID_LIST_PARAM_KEYS = [
    'statusIds', 'departmentIds', 'disciplineIds', 'phaseIds', 'communityIds', 'tagIds', 'projectIds',
] as const;

/**
 * Coerce every id list to positive integers. Saved custom views arrive as unparsed JSON
 * (getCustomFilter does no zod parse), so their ids can be strings — which contradict the request
 * schema and hash to a different query key than the numeric twin, caching the same result twice.
 */
const withNormalizedIdLists = <T extends Record<string, any>>(params: T): T => {
    const normalized: Record<string, any> = { ...params };
    for (const key of ID_LIST_PARAM_KEYS) {
        const ids = normalized[key];
        if (Array.isArray(ids)) normalized[key] = ids.map(Number).filter((id) => Number.isInteger(id) && id > 0);
    }
    return normalized as T;
};

/**
 * The month the calendar is currently showing. Deliberately NOT part of the search params: the
 * calendar reports its viewport through the same targetDate fields a user filter uses, so storing
 * it there means every view switch has to null those fields to get rid of it — which throws away
 * the user's own target date filter (the one a dashboard graph link arrives with).
 */
type CalendarRange = { start: Date | string | null; end: Date | string | null };

const NO_CALENDAR_RANGE: CalendarRange = { start: null, end: null };

const toDate = (value: Date | string | null | undefined) => (value ? Moment.utc(value) : null);

const laterOf = (a: Date | string | null | undefined, b: Date | string | null | undefined) => {
    const [ma, mb] = [toDate(a), toDate(b)];
    if (!ma) return mb;
    if (!mb) return ma;
    return ma.isAfter(mb) ? ma : mb;
};

const earlierOf = (a: Date | string | null | undefined, b: Date | string | null | undefined) => {
    const [ma, mb] = [toDate(a), toDate(b)];
    if (!ma) return mb;
    if (!mb) return ma;
    return ma.isBefore(mb) ? ma : mb;
};

/**
 * The params actually sent to (and keyed on by) the search query. The calendar requires
 * isCalendarView and plain date strings — the API only applies the target date range filter
 * in calendar mode. Kept module-level so callers that change the view can derive the exact
 * key the search will use before the re-render happens.
 */
const toEffectiveSearchParams = (
    params: TaskSearchRequest,
    viewName: string,
    calendarRange: CalendarRange = NO_CALENDAR_RANGE,
): TaskSearchRequest => {
    // The API takes a real boolean; the URL delivers the string 'true'
    const allLocations = isAllLocationsMode(params);
    if (viewName === VIEWS.Calendar.value) {
        // Intersect the visible month with the user's target date filter — the viewport narrows
        // what is on screen, it does not replace a filter the user (or a dashboard link) set.
        const start = laterOf(params.targetDateStart, calendarRange.start);
        const end = earlierOf(params.targetDateEnd, calendarRange.end);
        return {
            ...params,
            allLocations,
            isCalendarView: true,
            targetDateStart: start ? start.format(DATE_FORMAT) : null,
            targetDateEnd: end ? end.format(DATE_FORMAT) : null,
        };
    }
    return { ...params, allLocations, isCalendarView: false };
};

// ---- Inner component (uses Suspense query) ----

function TaskListPageContent() {
    const dispatch = useDispatch();
    const boundLocationDashboardActions = useMemo(() => bindActionCreators(locationDashboardActions, dispatch), [dispatch]);
    const history = useHistory();
    const location = useLocation();
    const queryClient = useQueryClient();

    // ── Redux state ──────────────────────────────────────────────────────────
    const windowWidth = useSelector((state: any) => state.window.width);
    const subscriptionId: string = useSelector((state: any) =>
        state.user.currentSubscriptionId ? String(state.user.currentSubscriptionId) : ''
    );
    const userRole: string = useSelector((state: any) => state.user.customerRole);
    const userId: number = useSelector((state: any) => state.user.id);
    const taskListState = useSelector((state: any) => state.taskListState);
    const searchParams: TaskSearchRequest = taskListState.searchParams;
    const tasksPageTasksMenuItems: unknown[] = taskListState.tasksPageTasksMenuItems ?? [];

    // ── Helpers ──────────────────────────────────────────────────────────────
    const isMobile = useCallback(() => windowWidth < MOBILE_WINDOW_BREAKPOINT, [windowWidth]);

    // ── Suspense query: list filters (feature flags, column definitions) ─────
    const { data: listFilters } = useLoadListFilters();

    // Ensure locationName always appears in savedColumns regardless of user preference
    const effectiveSavedColumns = useMemo(() => {
        const saved = [...(listFilters?.savedColumns ?? [])];
        if (saved.length > 0 && !saved.some((c: any) => c.fieldName === 'locationName')) {
            const locationNameCol = (listFilters?.masterColumns ?? []).find((c: any) => c.fieldName === 'locationName');
            if (locationNameCol) saved.push(locationNameCol);
        }
        return saved;
    }, [listFilters?.savedColumns, listFilters?.masterColumns]);

    const filters = useMemo(() => ({
        isStartDateActive: listFilters?.isStartDateActive ?? false,
        isTaskDependenciesActive: listFilters?.isTaskDependenciesActive ?? false,
        isTaskStepsActive: listFilters?.isTaskStepsActive ?? false,
        isProjectWidgetActive: listFilters?.isProjectWidgetActive ?? false,
        allowLocationTaskDateChanges: listFilters?.allowLocationTaskDateChanges ?? false,
        savedColumns: effectiveSavedColumns,
        masterColumns: listFilters?.masterColumns ?? [],
        calendarStartDay: ConvertDayOfWeekToCalendarStartDay(listFilters?.calendarStartDay),
        ganttZoomLevel: listFilters?.ganttZoomLevel ?? GANTT_ZOOM_LEVELS.Month.value,
        ganttExportToPDF: false,
        ganttExportToPNG: false,
        savedColumnOrder: listFilters?.savedColumnOrder ?? [],
    }), [listFilters, effectiveSavedColumns]);

    const isSaveAndManageTaskFiltersActive = listFilters?.isSaveAndManageTaskFiltersActive ?? false;

    // ── Sync menu items to Redux when list filters change ───────────────────
    useEffect(() => {
        if (!listFilters) return;
        const incoming = listFilters.filters ?? [];
        if (!_.isEqual(tasksPageTasksMenuItems, incoming)) {
            dispatch(locationTaskActions.setTasksPageTaskMenuItems(incoming));
        }
    }, [listFilters]);

    // ── URL param parsing & initialization ──────────────────────────────────
    const [isInitialized, setIsInitialized] = useState(false);
    // Tracks the params actually sent to the API. Only updated on explicit search triggers,
    // unlike searchParams (Redux) which updates immediately on each MultiSelect change for UI.
    const [committedSearchParams, setCommittedSearchParams] = useState<TaskSearchRequest>(() => searchParams);
    const hasOpenedUrlTask = useRef(false);
    const hasInitializedRef = useRef(false);

    const urlParams = useMemo(() => {
        const sp = new URLSearchParams(location.search);
        const params: Record<string, unknown> = {
            locationKeys: [],
            taskId: '',
            name: '',
            isMilestone: null,
            projectIds: [],
            targetDateStart: null,
            targetDateEnd: null,
            startDateStart: null,
            startDateEnd: null,
            responsibleUserName: '',
            statusIds: [],
            departmentIds: [],
            disciplineIds: [],
            phaseIds: [],
            communityIds: [],
            tagIds: [],
            recentlyUpdated: null,
            allTasks: null,
            // null, not false, so an absent la= is distinguishable from an explicit la=false
            allLocations: null,
            isFollowedByCurrentUser: null,
        };
        if (sp.has(URLParams.AllLocations)) params.allLocations = sp.get(URLParams.AllLocations);
        else if (sp.has(URLParams.LocationKeys)) params.locationKeys = sp.getAll(URLParams.LocationKeys);
        if (sp.has(URLParams.TaskId)) params.taskId = sp.get(URLParams.TaskId);
        if (sp.has(URLParams.IsMilestone)) params.isMilestone = sp.get(URLParams.IsMilestone);
        if (sp.has(URLParams.IsFollowedByCurrentUser)) params.isFollowedByCurrentUser = sp.get(URLParams.IsFollowedByCurrentUser);
        if (sp.has(URLParams.ProjectId)) params.projectIds = parseIdListParam(sp, URLParams.ProjectId);
        if (sp.has(URLParams.TargetDateStart)) params.targetDateStart = Moment(sp.get(URLParams.TargetDateStart)!).format(DATE_FORMAT);
        if (sp.has(URLParams.TargetDateEnd)) params.targetDateEnd = Moment(sp.get(URLParams.TargetDateEnd)!).format(DATE_FORMAT);
        if (sp.has(URLParams.StartDateStart)) params.startDateStart = Moment(sp.get(URLParams.StartDateStart)!).format(DATE_FORMAT);
        if (sp.has(URLParams.StartDateEnd)) params.startDateEnd = Moment(sp.get(URLParams.StartDateEnd)!).format(DATE_FORMAT);
        if (sp.has(URLParams.ResponsibleUserName)) params.responsibleUserName = sp.get(URLParams.ResponsibleUserName);
        if (sp.has(URLParams.StatusId)) params.statusIds = parseIdListParam(sp, URLParams.StatusId);
        if (sp.has(URLParams.DepartmentId)) params.departmentIds = parseIdListParam(sp, URLParams.DepartmentId);
        if (sp.has(URLParams.DisciplineId)) params.disciplineIds = parseIdListParam(sp, URLParams.DisciplineId);
        if (sp.has(URLParams.PhaseId)) params.phaseIds = parseIdListParam(sp, URLParams.PhaseId);
        if (sp.has(URLParams.CommunityId)) params.communityIds = parseIdListParam(sp, URLParams.CommunityId);
        if (sp.has(URLParams.RecentlyUpdated)) params.recentlyUpdated = sp.get(URLParams.RecentlyUpdated) !== 'false';
        if (sp.has(URLParams.AllTasks)) params.allTasks = sp.get(URLParams.AllTasks) !== 'false';
        if (sp.has(URLParams.TagId)) params.tagIds = parseIdListParam(sp, URLParams.TagId);
        if (sp.has(URLParams.Name)) params.name = sp.get(URLParams.Name);
        if (sp.has(URLParams.BaselineName)) params.baselineName = sp.get(URLParams.BaselineName);
        if (sp.has(URLParams.AdditionalInfo)) params.additionalInfo = sp.get(URLParams.AdditionalInfo);
        if (sp.has(URLParams.DropDownOption)) params.dropDownOption = sp.get(URLParams.DropDownOption);
        return params;
    }, []); // only parse once on mount

    const hasUrlParams = useCallback(() => {
        return urlParams && (
            (Array.isArray(urlParams.locationKeys) && urlParams.locationKeys.length > 0) ||
            (typeof urlParams.taskId === 'string' && urlParams.taskId.trim().length > 0) ||
            (Array.isArray(urlParams.projectIds) && urlParams.projectIds.length > 0) ||
            (typeof urlParams.targetDateStart === 'string' && urlParams.targetDateStart.trim().length > 0) ||
            (typeof urlParams.targetDateEnd === 'string' && urlParams.targetDateEnd.trim().length > 0) ||
            (typeof urlParams.startDateStart === 'string' && urlParams.startDateStart.trim().length > 0) ||
            (typeof urlParams.startDateEnd === 'string' && urlParams.startDateEnd.trim().length > 0) ||
            (typeof urlParams.responsibleUserName === 'string' && urlParams.responsibleUserName.trim().length > 0) ||
            (Array.isArray(urlParams.statusIds) && urlParams.statusIds.length > 0) ||
            (Array.isArray(urlParams.departmentIds) && urlParams.departmentIds.length > 0) ||
            (Array.isArray(urlParams.disciplineIds) && urlParams.disciplineIds.length > 0) ||
            (Array.isArray(urlParams.tagIds) && urlParams.tagIds.length > 0) ||
            urlParams.recentlyUpdated === true || urlParams.recentlyUpdated === false ||
            urlParams.allTasks === true || urlParams.allTasks === false ||
            /**
             * allLocations defaults to null and only receives a value (the string
             * 'true' or 'false') when la= is actually present on the URL, so any
             * non null value means the URL supplied it.
             */
            (urlParams.allLocations !== null && urlParams.allLocations !== undefined)
        );
    }, [urlParams]);

    // Load the default location when there's no persisted selection and no URL specifies one.
    // This ensures a fresh first-time visit auto-selects the user's default location.
    const persistedLocationKeys = taskListState?.searchParams?.locationKeys ?? [];
    // "All locations" mode is a location selection too — the default must not override it
    const urlHasLocation = (Array.isArray(urlParams.locationKeys) && urlParams.locationKeys.length > 0)
        || isAllLocationsMode(urlParams);
    const needsDefaultLocation = persistedLocationKeys.length === 0 && !urlHasLocation;

    const { data: locationSelectorData, isError: locationSelectorFailed } = useLoadInitialLocationSelectorFilters(
        { subscriptionId: Number(subscriptionId) },
        { enabled: !!subscriptionId && needsDefaultLocation }
    );

    useEffect(() => {
        if (hasInitializedRef.current) return;
        // Wait for the location selector if we need it to pick a default,
        // but never block initialization on a failed request
        if (needsDefaultLocation && !locationSelectorData && !locationSelectorFailed) return;

        hasInitializedRef.current = true;
        document.title = 'Task List';

        const defaultKey = locationSelectorData?.defaultLocationKey;
        const initialKeys = persistedLocationKeys.length > 0
            ? persistedLocationKeys
            : defaultKey ? [defaultKey] : [];

        let initial = makeDefaultSearchParams(initialKeys);

        if (hasUrlParams()) {
            const up = { ...urlParams } as any;

            /**
             * In "all locations" mode the empty locationKeys list is the whole point,
             * so it must not be back-filled from the previously selected location.
             */
            if (!isAllLocationsMode(up) && (!up.locationKeys || !up.locationKeys.length)) {
                up.locationKeys = initialKeys;
            }

            if (up.dropDownOption) {
                const menuItem = Object.values(TASK_LIST_MENU_ITEMS).find((i: any) => i.value === up.dropDownOption);
                if (menuItem) dispatch(locationTaskActions.setTasksMenuSelected(menuItem));
            }

            if (up.allLocations === 'false') {
                dispatch(locationTaskActions.setLocationFilterState({ ...taskListState.searchParams, locationKeys: [] }));
            }

            if (up.allTasks) {
                initial = makeDefaultSearchParams(initialKeys);
            } else {
                initial = { ...initial, ...up };
            }
        }

        dispatch(locationTaskActions.setLocationFilterState(initial));
        setCommittedSearchParams(initial);
        if (defaultKey && initialKeys.length > 0 && persistedLocationKeys.length === 0) {
            dispatch(locationTaskActions.setLocationKeys(initialKeys));
        }
        setIsInitialized(true);
    }, [locationSelectorData, locationSelectorFailed]); // re-evaluates when location data arrives (or fails)

    // ── Server-state queries ─────────────────────────────────────────────────

    // Column filters: re-fetch when location selection changes
    const columnFiltersLocationKeys = useMemo(
        () => determineLocationKeysForFilters(searchParams, taskListState.defaultLocationKey),
        [searchParams.locationKeys, searchParams.allLocations, taskListState.defaultLocationKey]
    );

    const isAllLocations = isAllLocationsMode(searchParams);

    const { data: columnFiltersRaw, isError: isColumnFiltersError } = useLoadColumnFilters(
        { subscriptionId: Number(subscriptionId), locationKeys: columnFiltersLocationKeys, allLocations: isAllLocations },
        {
            /**
             * On the first visit redux is empty and the header has not resolved
             * defaultLocationKey yet, so there is no location to filter on.  The endpoint
             * rejects an empty location list outside of "all locations" mode, so skip the
             * call entirely: the header applies the default location right after, which
             * runs this again with real keys.
             */
            enabled: isInitialized && !!subscriptionId && (isAllLocations || columnFiltersLocationKeys.length > 0),
        }
    );

    // Prune stale statusIds from searchParams when available filter options change
    useEffect(() => {
        if (!columnFiltersRaw || !isInitialized) return;
        // map(Number) before comparing, and keep the normalized value: ids can still arrive as
        // strings from a saved custom view (getCustomFilter returns unparsed JSON), and a string id
        // both contradicts the request schema and hashes to a different query key than its
        // numeric twin — the same result set fetched and cached twice.
        const pruneStatusIds = (ids: TaskSearchRequest['statusIds']) =>
            ids.map(Number).filter((id) => columnFiltersRaw.statuses.some((s) => s.id === id));

        const validStatusIds = pruneStatusIds(searchParams.statusIds);
        if (!isEqual(validStatusIds, searchParams.statusIds)) {
            dispatch(locationTaskActions.setLocationFilterState({ ...searchParams, statusIds: validStatusIds }));
        }
        // Prune the committed copy too. Redux alone would leave the page searching on a status the
        // filter UI no longer offers, and any commit that spreads committedSearchParams (a view
        // switch, a sort) would put the dead ids straight back into Redux.
        setCommittedSearchParams((prev) => {
            const validCommitted = pruneStatusIds(prev.statusIds);
            return isEqual(validCommitted, prev.statusIds) ? prev : { ...prev, statusIds: validCommitted };
        });
    }, [columnFiltersRaw?.statuses]);

    // Map API shape to the shape child components expect (targetDate vs targetDates)
    const columnFilters: ChildColumnFilters = useMemo(() => ({
        startDates: columnFiltersRaw?.startDates ?? [],
        targetDate: columnFiltersRaw?.targetDates ?? [],
        projects: columnFiltersRaw?.projects ?? [],
        communities: columnFiltersRaw?.communities ?? [],
        disciplines: columnFiltersRaw?.disciplines ?? [],
        departments: columnFiltersRaw?.departments ?? [],
        phases: columnFiltersRaw?.phases ?? [],
        statuses: columnFiltersRaw?.statuses ?? [],
        tags: columnFiltersRaw?.tags ?? [],
    }), [columnFiltersRaw]);

    // Selected view drives the search request shape (declared here because the
    // calendar view changes how the search request is built)
    const [selectedView, setSelectedView] = useState<string>(VIEWS.List.value);

    // The calendar's visible month, kept out of the search params on purpose — see CalendarRange
    const [calendarRange, setCalendarRange] = useState<CalendarRange>(NO_CALENDAR_RANGE);

    const effectiveSearchParams = useMemo<TaskSearchRequest>(
        () => toEffectiveSearchParams(committedSearchParams, selectedView, calendarRange),
        [committedSearchParams, selectedView, calendarRange]
    );

    // Main search (keepPreviousData avoids clearing the list on every filter change)
    const { data: searchData, isFetching: isSearching, isError: isSearchError } = useSearchLocationTasks(
        effectiveSearchParams,
        { enabled: isInitialized && !!subscriptionId }
    );

    const isMissingActiveLocations = searchData?.isMissingActiveLocations ?? false;

    // Responsible users for assignment drawers
    const locationKeysForUsers = useMemo(() => {
        const keys = searchParams.locationKeys;
        const selected = Array.isArray(keys) && keys.length > 0
            ? keys
            : taskListState.defaultLocationKey ? [taskListState.defaultLocationKey] : [];
        // defaultLocationKey is '' until the header loads it, so drop blanks before
        // the length check below decides there is something to send
        return selected.filter((key) => key !== null && key !== undefined && key !== '');
    }, [searchParams.locationKeys, taskListState.defaultLocationKey]);

    const { data: responsibleUsersData, isError: isResponsibleUsersError } = useSearchResponsibleUsers(
        locationKeysForUsers,
        '',
        { enabled: isInitialized && locationKeysForUsers.length > 0 }
    );
    const locationUsers: any[] = responsibleUsersData?.users ?? [];

    // A failed load leaves the list or its filters empty; say so instead of showing nothing
    useEffect(() => {
        if (isSearchError) Notification('error', 'Error', 'Tasks could not be loaded');
    }, [isSearchError]);

    useEffect(() => {
        if (isColumnFiltersError) Notification('error', 'Error', 'Task filters could not be loaded');
    }, [isColumnFiltersError]);

    useEffect(() => {
        if (isResponsibleUsersError) Notification('error', 'Error', 'Responsible users could not be loaded');
    }, [isResponsibleUsersError]);

    // Open task from URL once the first search completes
    useEffect(() => {
        if (!searchData || hasOpenedUrlTask.current) return;
        if (urlParams.taskId) {
            hasOpenedUrlTask.current = true;
            showTask(Number.parseInt(String(urlParams.taskId)));
        }
    }, [searchData]);

    // ── Local UI state ───────────────────────────────────────────────────────
    const [isTaskDetailOpen, setIsTaskDetailOpen] = useState(false);
    const [editTaskId, setEditTaskId] = useState(0);
    const [editLocationKey, setEditLocationKey] = useState('');
    const [isShiftKeyPressed, setIsShiftKeyPressed] = useState(false);
    // Quick create/edit sidebars; useLocationTaskSidebar keeps the detail drawer on the
    // read-only sidebar after a quick edit, even while shift is still held.
    const [isQuickCreateOpen, setIsQuickCreateOpen] = useState(false);
    const [isQuickEditOpen, setIsQuickEditOpen] = useState(false);
    const [useLocationTaskSidebar, setUseLocationTaskSidebar] = useState(false);
    const [checkedTaskIds, setCheckedTaskIds] = useState<Set<number>>(new Set());
    const [allTasksChecked, setAllTasksChecked] = useState(false);
    const [bulkUpdateMode, setBulkUpdateMode] = useState(false);
    const [isColumnFiltersDrawerOpen, setIsColumnFiltersDrawerOpen] = useState(false);
    const [isBulkUpdateResponsibleUserDrawerOpen, setIsBulkUpdateResponsibleUserDrawerOpen] = useState(false);
    const [isBulkUpdateStatusDrawerOpen, setIsBulkUpdateStatusDrawerOpen] = useState(false);
    const [isShowFiltersDrawerOpen, setIsShowFiltersDrawerOpen] = useState(false);
    const [isBulkUpdateByJobTitleOpen, setIsBulkUpdateByJobTitleOpen] = useState(false);
    const [customFiltersName, setCustomFiltersName] = useState('');
    const [isSaveFiltersModalFormValid, setIsSaveFiltersModalFormValid] = useState(true);
    const [shiftDays, setShiftDays] = useState('');
    const [shiftTargetDate, setShiftTargetDate] = useState<'yes' | 'no'>('yes');
    const [shiftStartDate, setShiftStartDate] = useState<'yes' | 'no'>('yes');

    const saveFiltersModal = useRef<any>(null);
    const shiftDatesModal = useRef<any>(null);

    // Reset checked state whenever search results change (new search = new selection)
    useEffect(() => {
        if (searchData) {
            setCheckedTaskIds(new Set());
            setAllTasksChecked(false);
        }
    }, [searchData?.results]);

    // Derived: tasks with client-side isChecked merged in
    const locationTasks: LocationTaskWithCheck[] = useMemo(
        () => (searchData?.results ?? []).map((t) => ({ ...t, isChecked: checkedTaskIds.has(t.id) })),
        [searchData?.results, checkedTaskIds]
    );
    const totalCount = searchData?.totalCount ?? 0;
    const checkedTasks = locationTasks.filter((t) => t.isChecked);

    // Default params for "clear all filters" — locations are preserved
    const defaultSearchParams = useMemo(
        () => makeDefaultSearchParams(searchParams.locationKeys),
        [searchParams.locationKeys]
    );
    const hasNoFilters = useMemo(() => isEqual(defaultSearchParams, searchParams), [defaultSearchParams, searchParams]);

    // ── Keyboard shift-key tracking ──────────────────────────────────────────
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== 'Shift') return;
            setIsShiftKeyPressed(e.type === 'keydown' && !isTaskDetailOpen);
        };
        document.addEventListener('keydown', onKey);
        document.addEventListener('keyup', onKey);
        return () => {
            document.removeEventListener('keydown', onKey);
            document.removeEventListener('keyup', onKey);
        };
    }, [isTaskDetailOpen]);

    // ── Mutations ────────────────────────────────────────────────────────────
    const { mutate: updateSavedColumnsMutate } = useUpdateSavedColumns();

    const { mutate: bulkShiftDatesMutate, isPending: isShiftDatesLoading } = useBulkShiftDates();

    const { mutate: updateIsActiveMutate } = useUpdateIsActive();
    const { mutate: updateFollowingTasksMutate } = useUpdateFollowingTasks();

    const { mutate: saveCustomFiltersMutate, isPending: isSaveCustomViewModalLoading } = useSaveCustomFilters();

    const { mutate: updateStatusMutate } = useUpdateStatus();
    const { mutate: updateResponsibleUserMutate } = useUpdateResponsibleUser();
    const { mutate: saveUserColumnsMutate } = useSaveUserColumns();
    const { mutate: downloadFileMutate } = useDownloadFile();

    // Inline "Assign to Me" from the Responsible User column
    const assignTaskToMe = useCallback((taskId: number) => {
        updateResponsibleUserMutate(
            { locationTaskIds: [taskId], responsibleUserId: userId },
            {
                onSuccess: () => Notification('success', 'Responsible User Updated', ''),
                onError: (err) => Notification('error', 'Error', err.message),
            }
        );
    }, [updateResponsibleUserMutate, userId]);

    // Inline status change from the Status column
    const changeTaskStatus = useCallback((taskId: number, statusId: number) => {
        updateStatusMutate(
            { locationTaskIds: [taskId], statusId },
            {
                onSuccess: () => {
                    // If status was set to core complete, show celebration confetti
                    const completeStatus = columnFilters.statuses.find(
                        (s) => s.coreStatusId === CORE_STATUSES.Complete
                    );
                    if (completeStatus && statusId === completeStatus.id) {
                        document.dispatchEvent(new Event(EVENT_TYPE.ConfettiCannon));
                    }
                    Notification('success', 'Status Updated', 'New Status has been set');
                },
                onError: (err) => Notification('error', 'Error', err.message),
            }
        );
    }, [updateStatusMutate, columnFilters.statuses]);

    const saveColumnOrder = useCallback((request: SaveUserColumnsRequest) => {
        saveUserColumnsMutate(request, {
            onSuccess: () => Notification('success', 'Success', 'Your column order has been saved.'),
            onError: (err) => Notification('error', 'Error', err.message),
        });
    }, [saveUserColumnsMutate]);

    const downloadFile = useCallback((fileName: string) => {
        downloadFileMutate(fileName, {
            onSuccess: () => Notification('success', 'Download Started', 'File download has been initiated.'),
            onError: (err) => Notification('error', 'Error', err.message),
        });
    }, [downloadFileMutate]);

    // ── Helpers ──────────────────────────────────────────────────────────────
    const syncUrlWithSearchParams = useCallback((params: TaskSearchRequest, changedKey?: string | null) => {
        const queryString = buildTaskListQueryString(params, changedKey ?? undefined);
        history.push({
            pathname: history.location.pathname,
            search: queryString ? `?${queryString}` : '',
        });
    }, [history]);

    /**
     * Commit an explicit set of params: Redux (what the filter UI shows), committedSearchParams
     * (what the API is actually asked for), the URL, and a targeted invalidation.
     *
     * Children that build their own params object must go through this rather than dispatching to
     * Redux and then triggering a search separately — the search would commit whatever the previous
     * render closed over, leaving the change in Redux only, and the next commit that spreads
     * committedSearchParams reverts it.
     */
    const applySearchParams = useCallback((newParams: TaskSearchRequest, changedKey: string | null = null) => {
        dispatch(locationTaskActions.setLocationFilterState(newParams));
        setCommittedSearchParams(newParams);
        syncUrlWithSearchParams(newParams, changedKey);
        invalidateSearch(queryClient, toEffectiveSearchParams(newParams, selectedView, calendarRange));
    }, [dispatch, syncUrlWithSearchParams, queryClient, selectedView, calendarRange]);

    const updateSearchParams = useCallback((key: string | null, value: unknown, search?: boolean) => {
        const currentParams = searchParams;
        const newParams: TaskSearchRequest = key ? { ...currentParams, [key]: value } : currentParams;
        const changed = key && !isEqual((newParams as any)[key], (searchParams as any)[key]);
        if (changed) {
            dispatch(locationTaskActions.setLocationFilterState(newParams));
        }
        if (search === true) {
            applySearchParams(newParams, key);
        }
    }, [searchParams, dispatch, applySearchParams]);

    const setLocationKeys = useCallback(async (locationKeys: string[]) => {
        // Picking locations explicitly leaves "all locations" mode
        const newParams = { ...searchParams, locationKeys, allLocations: false };
        dispatch(locationTaskActions.setLocationFilterState(newParams));
        dispatch(locationTaskActions.setLocationKeys(locationKeys));
        // Keep the committed copy in step even on the skipSearch path (the header applying its
        // default location): a divergence here means the page is searching a different location
        // than the selector shows, and the next commit would push the stale keys back into Redux.
        setCommittedSearchParams((prev) => (
            isEqual(prev.locationKeys, locationKeys) && prev.allLocations === false
                ? prev
                : { ...prev, locationKeys, allLocations: false }
        ));
    }, [searchParams, dispatch]);

    const getPrimaryLocation = useCallback(() => {
        const { locationKeys, } = searchParams;
        const { locations } = taskListState;
        if (locationKeys?.length === 1 && locations?.length > 0) {
            const primaryLocationKey = locationKeys[0];
            const found = locations.find((x: any) => x.id === primaryLocationKey);
            return found ? { ...found, locationKey: primaryLocationKey } : null;
        }
        return null;
    }, [searchParams.locationKeys, taskListState.locations]);

    const getSelectedLocations = useCallback(() => {
        return (searchParams.locationKeys ?? []).map((key) =>
            taskListState.locations?.find((l: any) => l.id === key)
        ).filter(Boolean);
    }, [searchParams.locationKeys, taskListState.locations]);

    // ── Task detail ──────────────────────────────────────────────────────────
    const showTask = useCallback((id: number) => {
        const task = (searchData?.results ?? []).find((t) => t.id === id);
        let locKey: string | null = null;
        if (task) {
            locKey = task.locationKey;
        } else {
            const primary = getPrimaryLocation();
            if (primary) locKey = (primary as any).locationKey;
        }
        setIsTaskDetailOpen(true);
        setEditTaskId(id);
        setEditLocationKey(locKey ?? '');
    }, [searchData?.results, getPrimaryLocation]);

    const closeTaskDetail = useCallback((triggerSearch = false) => {
        setIsTaskDetailOpen(false);
        setIsShiftKeyPressed(false);
        setUseLocationTaskSidebar(false);
        setEditTaskId(0);
        if (triggerSearch) invalidateSearches(queryClient);
    }, [queryClient]);

    // ── Quick create / quick edit ────────────────────────────────────────────
    const openQuickCreate = useCallback(() => setIsQuickCreateOpen(true), []);
    const onQuickCreateClose = useCallback(() => setIsQuickCreateOpen(false), []);

    // Shift-clicking Add Task falls back to the full task detail drawer
    const openAddTaskLegacy = useCallback(() => {
        const primary = getPrimaryLocation();
        const locKey = primary ? (primary as any).locationKey : (searchParams.locationKeys[0] ?? '');
        setIsTaskDetailOpen(true);
        setEditTaskId(0);
        setEditLocationKey(locKey);
    }, [getPrimaryLocation, searchParams.locationKeys]);

    const onEditLocationTask = useCallback(() => {
        setIsQuickEditOpen(true);
        setIsTaskDetailOpen(false);
    }, []);

    const onQuickEditClose = useCallback(() => {
        setIsQuickEditOpen(false);
        setIsTaskDetailOpen(true);
    }, []);

    const onQuickTaskSaved = useCallback((taskId: number, locationKey: string) => {
        setIsQuickCreateOpen(false);
        setIsQuickEditOpen(false);
        setIsTaskDetailOpen(true);
        setEditTaskId(taskId);
        setEditLocationKey(locationKey);
        setUseLocationTaskSidebar(true);
        invalidateSearches(queryClient);
    }, [queryClient]);

    // ── View management ──────────────────────────────────────────────────────
    const onViewChange = useCallback((viewName: string) => {
        // Start from the params the current view was actually searched with, not from the Redux
        // copy: Redux also holds uncommitted UI-only edits and is rewritten by async effects
        // (e.g. the prune-stale-statusIds effect), so basing the request on it can silently
        // drop an active filter — a completed-milestone leak on the Gantt tab, for instance.
        let newParams = { ...committedSearchParams };

        // Only paging and sort differ per view — every filter carries over via the spread. Nothing
        // here may null targetDateStart/End: those hold the user's own filter (a dashboard graph
        // links straight into one), and the calendar's viewport lives in calendarRange instead.
        if (viewName === VIEWS.List.value) {
            newParams = { ...newParams, pageNumber: 1, pageSize: DEFAULT_PAGE_SIZE, sortBy: 'targetDate' };
        } else if (viewName === VIEWS.Calendar.value) {
            newParams = { ...newParams, pageNumber: 1, pageSize: 1000 };
            ReactGA.event({ category: 'Task List Page', action: 'Toggle Calendar' });
        } else if (viewName === VIEWS.Gantt.value) {
            newParams = { ...newParams, pageNumber: 1, pageSize: 10000, sortBy: 'LocationId, ProjectName, TargetDate' };
            ReactGA.event({ category: 'Task List Page', action: 'Toggle Gantt Chart' });
        } else if (viewName === VIEWS.ResourceAllocation.value) {
            ReactGA.event({ category: 'Task List Page', action: 'Toggle Workload View' });
        }

        setSelectedView(viewName);
        dispatch(locationTaskActions.setLocationFilterState(newParams));
        setCommittedSearchParams(newParams);
        // No invalidation: a view switch changes no filter, so a view whose result is already
        // cached is shown from cache. Only a param change (or a mutation) refetches.
    }, [committedSearchParams, dispatch]);

    const onLocationChange = useCallback(async (locationKeys: string[], skipSearch = false) => {
        await setLocationKeys(locationKeys);
        if (!skipSearch) {
            const newParams = { ...searchParams, locationKeys, allLocations: false, pageNumber: 1 };
            dispatch(locationTaskActions.setLocationFilterState(newParams));
            setCommittedSearchParams(newParams);
            syncUrlWithSearchParams(newParams, 'locationKeys');
            invalidateSearch(queryClient, toEffectiveSearchParams(newParams, selectedView, calendarRange));
        }
    }, [searchParams, setLocationKeys, dispatch, syncUrlWithSearchParams, queryClient, selectedView, calendarRange]);

    // The calendar reporting the month it moved to. This is a viewport, not a filter: it must not
    // touch the search params, or paging away in the calendar would rewrite the user's target date
    // filter — and every other view would then have to null those fields to undo it.
    const onCalendarDateChange = useCallback(async (start: Date, end: Date) => {
        // FullCalendar hands us fresh Date objects on every datesSet, so identity comparison never
        // matches and the range (and every callback that depends on it) is rebuilt each time.
        setCalendarRange((prev) => {
            const sameStart = toDate(prev.start)?.valueOf() === toDate(start)?.valueOf();
            const sameEnd = toDate(prev.end)?.valueOf() === toDate(end)?.valueOf();
            return sameStart && sameEnd ? prev : { start, end };
        });
    }, []);

    const onColumnSort = useCallback((sortBy: string, sortDir: string) => {
        if (searchParams.sortBy !== sortBy || searchParams.sortDir !== sortDir) {
            const newParams = { ...searchParams, sortBy, sortDir };
            dispatch(locationTaskActions.setLocationFilterState(newParams));
            setCommittedSearchParams(newParams);
            invalidateSearch(queryClient, toEffectiveSearchParams(newParams, selectedView, calendarRange));
        }
    }, [searchParams, dispatch, queryClient, selectedView, calendarRange]);

    // ── Column management ────────────────────────────────────────────────────
    const onColumnFiltersComplete = useCallback(async (arrayOfColumnIds: number[]) => {
        setIsColumnFiltersDrawerOpen(false);
        updateSavedColumnsMutate({ columnIds: arrayOfColumnIds }, {
            onError: (err) => Notification('error', 'Error', err.message),
        });
    }, [updateSavedColumnsMutate]);

    const onResetColumnFilters = useCallback(() => {
        const ids = (filters.masterColumns as any[]).map((c) => c.columnId);
        onColumnFiltersComplete(ids);
    }, [filters.masterColumns, onColumnFiltersComplete]);

    // ── Checkbox / bulk selection ────────────────────────────────────────────
    const onToggleCheckedTask = useCallback((locationTaskId: number) => {
        setCheckedTaskIds((prev) => {
            const next = new Set(prev);
            if (next.has(locationTaskId)) next.delete(locationTaskId);
            else next.add(locationTaskId);
            const all = (searchData?.results ?? []).every((t) => next.has(t.id));
            setAllTasksChecked(all);
            return next;
        });
    }, [searchData?.results]);

    const onMasterCheckboxToggle = useCallback(() => {
        const newChecked = !allTasksChecked;
        const results = searchData?.results ?? [];
        setCheckedTaskIds(newChecked ? new Set(results.map((t) => t.id)) : new Set());
        setAllTasksChecked(newChecked);
    }, [allTasksChecked, searchData?.results]);

    const clearAllCheckedTasks = useCallback(() => {
        setCheckedTaskIds(new Set());
        setAllTasksChecked(false);
    }, []);

    // ── Bulk operations ──────────────────────────────────────────────────────
    const requireCheckedTasks = (action: () => void) => {
        if (checkedTasks.length === 0) {
            Notification('error', 'Use the checkboxes in the table to select some Tasks first.', '');
            return false;
        }
        action();
        return true;
    };

    const bulkUpdateComplete = useCallback(() => {
        setIsBulkUpdateResponsibleUserDrawerOpen(false);
        setIsBulkUpdateStatusDrawerOpen(false);
        invalidateSearches(queryClient);
    }, [queryClient]);

    const bulkUpdateInactive = useCallback(() => {
        if (checkedTasks.length === 0) {
            Notification('error', 'Use the checkboxes in the table to select some Tasks first.', '');
            return;
        }
        updateIsActiveMutate(
            { locationTaskIds: checkedTasks.map((t) => t.id) },
            {
                onSuccess: (result) => {
                    if (result.isError) {
                        Notification('error', 'Error', 'Failed to update tasks');
                    } else if ((result.invalidLocationTasks?.length ?? 0) > 0) {
                        const names = (result.invalidLocationTasks ?? []).map((t) => t.name);
                        Notification('error', 'Location Task Contingent Violation',
                            'The relationship to the prerequisite needs to be removed before this task(s) can be made inactive: <br/>' + names.join('</br>'));
                    } else {
                        Notification('success', 'Success', 'Tasks(s) successfully updated');
                        bulkUpdateComplete();
                    }
                },
                onError: (err) => Notification('error', 'Error', err.message),
            }
        );
    }, [checkedTasks, updateIsActiveMutate, bulkUpdateComplete]);

    const bulkUpdateFollowTask = useCallback((selectedBulkValue: string) => {
        if (checkedTasks.length === 0) {
            Notification('error', 'Use the checkboxes in the table to select some Tasks first.', '');
            return;
        }
        const unfollow = selectedBulkValue === 'UnfollowTask';
        updateFollowingTasksMutate(
            { locationTaskIds: checkedTasks.map((t) => t.id), unfollow },
            {
                onSuccess: (result) => {
                    // The endpoint reports success/failure through isError only; it returns no row count.
                    if (result.isError) {
                        Notification('error', 'Error', `Tasks could not be ${unfollow ? 'unfollowed' : 'followed'}`);
                    } else {
                        Notification('success', 'Success', `Tasks(s) successfully ${unfollow ? 'unfollowed' : 'followed'}`);
                        bulkUpdateComplete();
                    }
                },
                onError: (err) => Notification('error', 'Error', err.message),
            }
        );
    }, [checkedTasks, updateFollowingTasksMutate, bulkUpdateComplete]);

    // ── Shift dates ──────────────────────────────────────────────────────────
    const shiftDatesHandler = useCallback(() => {
        if (checkedTasks.length === 0) {
            Notification('error', 'Use the checkboxes in the table to select some Tasks first.', '');
            shiftDatesModal.current?.hide();
            return;
        }
        shiftDatesModal.current?.show();
    }, [checkedTasks]);

    const validateTaskDateShiftRules = useCallback(
        (tasks: LocationTaskWithCheck[], daysToShift: number): ShiftDateValidationResult => {
            const checkedIds = tasks.map((t) => t.id);
            const getNewTargetDate = (task: LocationTask) => {
                if (!task.targetDate) return null;
                const d = Moment(task.targetDate);
                return checkedIds.includes(task.id) && shiftTargetDate === 'yes'
                    ? d.clone().add(daysToShift, 'days')
                    : d;
            };

            for (const task of tasks) {
                const curTarget = task.targetDate ? Moment(task.targetDate) : null;
                const curStart = task.startDate ? Moment(task.startDate) : null;
                const newTarget = shiftTargetDate === 'yes' && curTarget ? curTarget.clone().add(daysToShift, 'days') : curTarget;
                const newStart = shiftStartDate === 'yes' && curStart ? curStart.clone().add(daysToShift, 'days') : curStart;
                const finalStart = newStart ?? curStart;
                const finalTarget = newTarget ?? curTarget;

                if (finalStart && finalTarget && finalStart.isAfter(finalTarget)) {
                    return { isValid: false, message: `Task "${task.name}" would have a start date after its target date.` };
                }

                const contingentId = (task as any).contingentId;
                if (contingentId && newTarget) {
                    const contingentTask = locationTasks.find((t) => t.id === contingentId);
                    if (contingentTask?.targetDate) {
                        const contingentNew = getNewTargetDate(contingentTask);
                        if (contingentNew && contingentNew.isSameOrBefore(newTarget)) {
                            return { isValid: false, message: `Task "${task.name}" has a dependency on "${contingentTask.name}" whose target date must be after the shifted date.` };
                        }
                    }
                }

                if (newTarget) {
                    const dependents = locationTasks.filter((t) => (t as any).contingentId === task.id);
                    for (const dep of dependents) {
                        if (dep.targetDate) {
                            const depNew = getNewTargetDate(dep);
                            if (depNew && newTarget.isAfter(depNew)) {
                                return { isValid: false, message: `Task "${task.name}" is a dependency for "${dep.name}" whose target date would be violated.` };
                            }
                        }
                    }
                }
            }
            return { isValid: true };
        },
        [locationTasks, shiftTargetDate, shiftStartDate]
    );

    const onShiftDates = useCallback(() => {
        if (isShiftDatesLoading) return;
        if (checkedTasks.length === 0) { Notification('error', 'Select tasks first.', ''); return; }
        if (!shiftDays || shiftDays.trim() === '') { Notification('error', 'Please enter the number of days to shift.', ''); return; }
        const days = parseInt(shiftDays);
        if (isNaN(days) || days === 0) { Notification('error', 'Please enter a valid non-zero number of days.', ''); return; }
        if (shiftTargetDate === 'no' && shiftStartDate === 'no') { Notification('error', 'Please select at least one date type to shift.', ''); return; }

        const validation = validateTaskDateShiftRules(checkedTasks, days);
        if (!validation.isValid) { Notification('error', 'Date Shift Validation Error', validation.message ?? ''); return; }

        bulkShiftDatesMutate(
            {
                locationTaskIds: checkedTasks.map((t) => t.id),
                daysToShift: days,
                shiftTargetDates: shiftTargetDate === 'yes',
                shiftStartDates: shiftStartDate === 'yes',
            },
            {
                onSuccess: () => {
                    Notification('success', 'Success', `Task dates shifted by ${days} days`);
                    onShiftDatesModalClose();
                },
                onError: (err) => Notification('error', 'An Error Occurred', err.message),
            }
        );
    }, [isShiftDatesLoading, checkedTasks, shiftDays, shiftTargetDate, shiftStartDate, validateTaskDateShiftRules, bulkShiftDatesMutate]);

    const onShiftDatesModalClose = useCallback(() => {
        setShiftDays('');
        setShiftTargetDate('yes');
        setShiftStartDate('yes');
        clearAllCheckedTasks();
        shiftDatesModal.current?.hide();
    }, [clearAllCheckedTasks]);

    // ── Filter management ────────────────────────────────────────────────────
    const clearAllFilters = useCallback(async () => {
        dispatch(locationTaskActions.setTasksMenuSelected(TASK_LIST_MENU_ITEMS.Tasks));
        // Clearing filters must not silently drop the location scope: in all-locations mode
        // locationKeys is empty, so the defaults alone would leave the search unscoped.
        const cleaned = {
            ...makeDefaultSearchParams(searchParams.locationKeys),
            allLocations: isAllLocationsMode(searchParams),
        };
        // Go through applySearchParams so the URL is cleared too - otherwise a refresh or Back
        // restores every filter that was just cleared.
        applySearchParams(cleaned);
    }, [searchParams, dispatch, applySearchParams]);

    const onFiltersDrawerComplete = useCallback(async (newParams: Partial<TaskSearchRequest>) => {
        // pageNumber resets because the new filter set can be shorter than the current page index,
        // which would ask for a page past the end and render an empty grid beside a non-zero total.
        const merged = { ...searchParams, ...newParams, pageNumber: 1 };
        setIsShowFiltersDrawerOpen(false);
        applySearchParams(merged);
    }, [searchParams, applySearchParams]);

    // ── Custom views / saved filters ─────────────────────────────────────────
    const onSaveViewClick = useCallback(() => saveFiltersModal.current?.show(), []);

    const onSaveCustomFilters = useCallback(() => {
        if (customFiltersName.trim().length < 1) {
            setIsSaveFiltersModalFormValid(false);
            return;
        }
        saveCustomFiltersMutate(
            {
                filterName: customFiltersName.trim(),
                // Persist the mode as a real boolean so a reloaded view keeps working
                searchParams: { ...searchParams, allLocations: isAllLocationsMode(searchParams) } as unknown as Record<string, unknown>,
            },
            {
                onSuccess: () => {
                    Notification('success', 'Save Successful', 'Custom View created');
                    saveFiltersModal.current?.hide();
                    const newItems = structuredClone(tasksPageTasksMenuItems);
                    (newItems as any[]).push({ filterName: customFiltersName.trim(), isCustom: true });
                    dispatch(locationTaskActions.setTasksPageTaskMenuItems(newItems));
                },
                onError: (err) => Notification('error', 'An Error Occurred', err.message),
            }
        );
    }, [customFiltersName, saveCustomFiltersMutate, searchParams, tasksPageTasksMenuItems, dispatch]);

    const onCustomViewClick = useCallback(async (filterName: string) => {
        try {
            const response = await getCustomFilter(filterName) as any;

            let newParams: any = withNormalizedIdLists({ ...defaultSearchParams, ...response.filters });
            newParams.targetDateEnd = newParams.targetDateEnd ? Moment(newParams.targetDateEnd).format(DATE_FORMAT) : null;
            newParams.targetDateStart = newParams.targetDateStart ? Moment(newParams.targetDateStart).format(DATE_FORMAT) : null;
            newParams.startDateEnd = newParams.startDateEnd ? Moment(newParams.startDateEnd).format(DATE_FORMAT) : null;
            newParams.startDateStart = newParams.startDateStart ? Moment(newParams.startDateStart).format(DATE_FORMAT) : null;

            const hasLocationChanged = !isEqual(newParams.locationKeys, searchParams.locationKeys);

            dispatch(locationTaskActions.setLocationFilterState(newParams));
            setCommittedSearchParams(newParams);
            syncUrlWithSearchParams(newParams);
            invalidateSearch(queryClient, toEffectiveSearchParams(newParams, selectedView, calendarRange));

            if (hasLocationChanged) {
                dispatch(locationTaskActions.setLocationKeys(newParams.locationKeys));
                invalidateColumnFilters(queryClient);
            }
        } catch (err) {
            Notification('error', 'An Error Occurred', String(err));
        }
    }, [defaultSearchParams, searchParams.locationKeys, dispatch, syncUrlWithSearchParams, queryClient, selectedView, calendarRange]);

    // ── View renderer ─────────────────────────────────────────────────────────
    const { mutateAsync: updateDatesMutateAsync } = useUpdateDates();
    const { mutateAsync: updateContingentMutateAsync } = useUpdateContingent();
    const isLoadingAnything = isSearching;
    const lastSearchTime = useMemo(() => new Date(), [searchData]);

    const renderView = () => {
        switch (selectedView) {
            case VIEWS.Kanban.value:
                return <KanbanView locationTasks={locationTasks} totalCount={totalCount} />;

            case VIEWS.Calendar.value:
                return (
                    <CalendarView
                        locationTasks={locationTasks}
                        onSelectTask={(task: any) => showTask(task?.id ?? task)}
                        isFetchingTasks={isSearching}
                        columnFilters={columnFilters}
                        settings={filters}
                        updateTaskDates={async (taskId: number, start: string, target: string) => {
                            try {
                                await updateDatesMutateAsync({ taskId, startDate: start, targetDate: target });
                                Notification('success', '', 'Task updated');
                            } catch (err) {
                                Notification('error', 'Error', (err as Error).message);
                            }
                        }}
                        lastSearchTime={lastSearchTime}
                        onCalendarDateChange={onCalendarDateChange}
                        onStatusChangedComplete={() => invalidateSearches(queryClient)}
                    />
                );

            case VIEWS.Gantt.value:
                return (
                    <GanttChartView2
                        locationTasks={locationTasks}
                        onSelectTask={(task: any) => showTask(task?.id ?? task)}
                        isFetchingTasks={isSearching}
                        columnFilters={columnFilters}
                        settings={filters}
                        locations={getSelectedLocations()}
                        updateTaskDates={async (taskId: number, start: string, target: string) => {
                            try {
                                await updateDatesMutateAsync({ taskId, startDate: start, targetDate: target });
                                Notification('success', '', 'Task updated');
                            } catch (err) {
                                Notification('error', 'Error', (err as Error).message);
                            }
                        }}
                        updateContingent={async (taskId: number, contingentId: number | null) => {
                            try {
                                await updateContingentMutateAsync({ taskId, contingentId });
                                Notification('success', '', 'Task updated');
                            } catch (err) {
                                Notification('error', 'Error', (err as Error).message);
                            }
                        }}
                        lastSearchTime={lastSearchTime}
                        windowWidth={windowWidth}
                    />
                );

            case VIEWS.ResourceAllocation.value:
                return (
                    <ResourceAllocationView
                        settings={filters}
                        locations={getSelectedLocations()}
                        searchParams={searchParams}
                        showTask={showTask}
                    />
                );

            case VIEWS.List.value:
            default:
                if (isMobile()) {
                    return (
                        <ListViewMobile
                            locationTasks={locationTasks}
                            totalCount={totalCount}
                            filters={filters}
                            onSelectTask={(task: any) => showTask(task?.id ?? task)}
                            isFetchingTasks={isSearching}
                            columnFilters={columnFilters}
                            searchParams={searchParams}
                            updateSearchParams={updateSearchParams}
                        />
                    );
                }
                return (
                    <ListView
                        onApplySearchParams={applySearchParams}
                        assignTaskToMe={assignTaskToMe}
                        changeTaskStatus={changeTaskStatus}
                        saveColumnOrder={saveColumnOrder}
                        downloadFile={downloadFile}
                        responsibleUsers={locationUsers}
                        isLoading={isLoadingAnything}
                        locationTasks={locationTasks}
                        totalCount={totalCount}
                        filters={filters}
                        columnFilters={columnFilters}
                        searchParams={searchParams}
                        allTasksChecked={allTasksChecked}
                        onSort={onColumnSort}
                        updateSearchParams={updateSearchParams}
                        clearAllFilters={clearAllFilters}
                        onMasterCheckboxToggle={onMasterCheckboxToggle}
                        onToggleCheckedTask={onToggleCheckedTask}
                        showTask={showTask}
                        bulkUpdateMode={bulkUpdateMode}
                        userId={userId}
                        bulkUpdateStatus={() => requireCheckedTasks(() => setIsBulkUpdateStatusDrawerOpen(true))}
                        bulkUpdateResponsibleUser={() => requireCheckedTasks(() => setIsBulkUpdateResponsibleUserDrawerOpen(true))}
                        bulkUpdateFollowTask={bulkUpdateFollowTask}
                        bulkUpdateByJobTitle={() => setIsBulkUpdateByJobTitleOpen(true)}
                        bulkUpdateInactive={bulkUpdateInactive}
                        bulkShiftDates={shiftDatesHandler}
                        onGanttExportToPDF={() => {}}
                        onColumnsClick={() => setIsColumnFiltersDrawerOpen(true)}
                        userRole={userRole}
                        checkedTasks={checkedTasks}
                    />
                );
        }
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className='task-list-page'>
            {isInitialized && (
                <TaskListHeader
                    isMobile={isMobile()}
                    subscriptionId={subscriptionId}
                    taskListState={taskListState}
                    urlParams={urlParams}
                    selectedView={selectedView}
                    onViewChange={onViewChange}
                    onLocationChange={onLocationChange}
                    onColumnsClick={() => setIsColumnFiltersDrawerOpen(true)}
                    onFiltersClick={() => setIsShowFiltersDrawerOpen(true)}
                    bulkUpdateMode={bulkUpdateMode}
                    changeBulkUpdateMode={() => {
                        ReactGA.event({ category: 'Task List Page', action: `Toggle Bulk Update ${bulkUpdateMode ? 'Off' : 'On'}` });
                        setBulkUpdateMode((prev) => !prev);
                    }}
                    bulkUpdateInactive={bulkUpdateInactive}
                    bulkUpdateStatus={() => requireCheckedTasks(() => setIsBulkUpdateStatusDrawerOpen(true))}
                    bulkUpdateResponsibleUser={() => requireCheckedTasks(() => setIsBulkUpdateResponsibleUserDrawerOpen(true))}
                    bulkUpdateByJobTitle={() => setIsBulkUpdateByJobTitleOpen(true)}
                    hasNoFilters={hasNoFilters}
                    onClearFilters={clearAllFilters}
                    userRole={userRole}
                    onAddNewTask={openQuickCreate}
                    onAddNewTaskLegacy={openAddTaskLegacy}
                    onSaveViewClick={onSaveViewClick}
                    onCustomViewClick={onCustomViewClick}
                    history={history}
                    isSaveAndManageTaskFiltersActive={isSaveAndManageTaskFiltersActive}
                />
            )}

            <div>
                {isMissingActiveLocations ? (
                    <div className='alert-danger'>
                        <p>
                            Your account does not have permission to access any active locations. Please{' '}
                            <a href='#' onClick={(e) => e.preventDefault()}>submit a Support Ticket</a>{' '}
                            to your subscription administrator(s) to resolve this issue.
                        </p>
                    </div>
                ) : (
                    renderView()
                )}

                {isShiftKeyPressed && !useLocationTaskSidebar && isTaskDetailOpen ? (
                    <AnyTaskDetailManager
                        taskId={editTaskId}
                        locationKey={editLocationKey}
                        userId={userId}
                        responsibleUsers={locationUsers}
                        customerRole={userRole}
                        onComplete={() => closeTaskDetail(true)}
                        onCancel={() => closeTaskDetail(false)}
                        filesActions={filesActions}
                        locationTaskActions={locationTaskActions}
                        isMobile={isMobile()}
                    />
                ) : (
                    <LocationTaskSidebar
                        locationTaskId={editTaskId}
                        changeTask={setEditTaskId}
                        locationKey={editLocationKey}
                        isDrawerVisible={isTaskDetailOpen}
                        responsibleUsers={locationUsers}
                        closeDrawer={() => closeTaskDetail(false)}
                        onComplete={() => closeTaskDetail(true)}
                        isMobile={isMobile()}
                        onEditTask={onEditLocationTask}
                    />
                )}

                {isQuickCreateOpen && (
                    <QuickCreateTaskSidebar
                        responsibleUsers={locationUsers}
                        isDrawerVisible={isQuickCreateOpen}
                        closeDrawer={onQuickCreateClose}
                        onTaskCreated={onQuickTaskSaved}
                        locationKey={searchParams.locationKeys?.[0] ?? ''}
                        userId={userId}
                        currentProjectIds={searchParams.projectIds ?? []}
                        currentDepartmentIds={searchParams.departmentIds ?? []}
                    />
                )}

                {isQuickEditOpen && (
                    <QuickCreateTaskSidebar
                        responsibleUsers={locationUsers}
                        isDrawerVisible={isQuickEditOpen}
                        closeDrawer={onQuickEditClose}
                        onTaskCreated={onQuickTaskSaved}
                        onTaskUpdated={onQuickTaskSaved}
                        locationKey={editLocationKey}
                        userId={userId}
                        currentProjectIds={searchParams.projectIds ?? []}
                        currentDepartmentIds={searchParams.departmentIds ?? []}
                        editLocationTaskId={editTaskId}
                    />
                )}

                {isColumnFiltersDrawerOpen && (
                    <ColumnFiltersDrawer
                        onComplete={onColumnFiltersComplete}
                        onCancel={() => setIsColumnFiltersDrawerOpen(false)}
                        onReset={onResetColumnFilters}
                        filters={filters}
                        locationTasks={locationTasks}
                        searchParams={searchParams}
                    />
                )}

                {isBulkUpdateResponsibleUserDrawerOpen && (
                    <BulkUpdateResponsibleUserDrawer
                        onComplete={bulkUpdateComplete}
                        onCancel={() => { setIsBulkUpdateResponsibleUserDrawerOpen(false); setIsBulkUpdateStatusDrawerOpen(false); }}
                        locationTasks={checkedTasks}
                        locationKeys={searchParams.locationKeys}
                        responsibleUsers={locationUsers}
                        userId={userId}
                    />
                )}

                {isBulkUpdateStatusDrawerOpen && (
                    <BulkUpdateStatusDrawer
                        onComplete={bulkUpdateComplete}
                        onCancel={() => { setIsBulkUpdateResponsibleUserDrawerOpen(false); setIsBulkUpdateStatusDrawerOpen(false); }}
                        statuses={columnFilters.statuses}
                        locationTasks={checkedTasks}
                    />
                )}

                {isShowFiltersDrawerOpen && (
                    <ShowFiltersDrawer
                        onComplete={onFiltersDrawerComplete}
                        onCancel={() => setIsShowFiltersDrawerOpen(false)}
                        columnFilters={columnFilters}
                        searchParams={searchParams}
                        updateSearchParams={updateSearchParams}
                        isStartDateActive={filters.isStartDateActive}
                    />
                )}


                {isBulkUpdateByJobTitleOpen && (
                    <AssignTasksDrawer
                        locationDashboardActions={boundLocationDashboardActions}
                        locationKey={searchParams.locationKeys.toString()}
                        onComplete={() => { setIsBulkUpdateByJobTitleOpen(false); invalidateSearches(queryClient); }}
                        onCancel={() => setIsBulkUpdateByJobTitleOpen(false)}
                    />
                )}

                <br />
                <ReportFooter subscriptionActions={subscriptionActions} />
            </div>

            {/* Save View Modal */}
            <Modal61
                title='Save View'
                ref={saveFiltersModal}
                body={
                    <form name='saveViewForm' noValidate className={isSaveFiltersModalFormValid ? '' : 'was-validated'}>
                        <div className='mb-3'>
                            <label htmlFor='custom-filters-name' className='title'>Custom View Name</label>
                            <InputText
                                name='custom-filters-name'
                                type='text'
                                value={customFiltersName}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCustomFiltersName(e.target.value)}
                                isRequired={true}
                                errorMessage='Custom View Name is required'
                                maxLength={20}
                                placeholder='What would you like to call this view?'
                            />
                            <small className='form-text text-muted sub-title'>Character Limit 20</small>
                        </div>
                    </form>
                }
                primaryButtonText={isSaveCustomViewModalLoading ? 'Saving...' : 'Save'}
                onPrimaryClick={onSaveCustomFilters}
            />

            {/* Shift Task Dates Modal */}
            <Modal61
                title='Shift Task Dates'
                ref={shiftDatesModal}
                body={
                    <form name='shiftDatesForm' className='shift-dates-form mx-auto p-4 rounded' style={{ maxWidth: '500px' }} noValidate>
                        <div className='mb-4 d-flex align-items-center'>
                            <label className='form-label me-3 mb-0' style={{ minWidth: '200px' }}>
                                How many days to shift these tasks? (+ to add, - to subtract)
                            </label>
                            <div className='d-flex align-items-center'>
                                <input
                                    type='number'
                                    className='form-control me-2'
                                    style={{ width: '100px' }}
                                    value={shiftDays || ''}
                                    onChange={(e) => setShiftDays(e.target.value)}
                                    placeholder='0'
                                />
                                <span className='text-muted'>Days</span>
                            </div>
                        </div>
                        <div className='mb-3 d-flex align-items-center'>
                            <span className='me-3' style={{ minWidth: '200px' }}>Shift Target Date</span>
                            {(['no', 'yes'] as const).map((v) => (
                                <div key={v} className='form-check form-check-inline me-3'>
                                    <input className='form-check-input custom-radio' type='radio' name='shiftTargetDate'
                                        id={`shiftTarget${v}`} value={v} checked={shiftTargetDate === v}
                                        onChange={(e) => setShiftTargetDate(e.target.value as 'yes' | 'no')} />
                                    <label className='form-check-label' htmlFor={`shiftTarget${v}`}>{v === 'yes' ? 'Yes' : 'No'}</label>
                                </div>
                            ))}
                        </div>
                        <div className='d-flex align-items-center'>
                            <span className='me-3' style={{ minWidth: '200px' }}>Shift Start Date</span>
                            {(['no', 'yes'] as const).map((v) => (
                                <div key={v} className='form-check form-check-inline me-3'>
                                    <input className='form-check-input custom-radio' type='radio' name='shiftStartDate'
                                        id={`shiftStart${v}`} value={v} checked={shiftStartDate === v}
                                        onChange={(e) => setShiftStartDate(e.target.value as 'yes' | 'no')} />
                                    <label className='form-check-label' htmlFor={`shiftStart${v}`}>{v === 'yes' ? 'Yes' : 'No'}</label>
                                </div>
                            ))}
                        </div>
                    </form>
                }
                showSecondaryButton={true}
                primaryButtonText={isShiftDatesLoading ? 'Saving...' : 'Change Dates'}
                secondaryButtonText='Cancel'
                onPrimaryClick={onShiftDates}
                onSecondaryClick={onShiftDatesModalClose}
                onCloseClick={onShiftDatesModalClose}
            />
        </div>
    );
}

// ---- Outer component: ErrorBoundary + Suspense shell ----

export function TaskListPage() {
    return (
        <ErrorBoundary FallbackComponent={ErrorFallback}>
            <Suspense fallback={<LoadingIndicator isLoading={true} />}>
                <TaskListPageContent />
            </Suspense>
        </ErrorBoundary>
    );
}

export default TaskListPage;
